import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { useConfig } from './configContext';
import { useVersion } from '../components/sidebar/useVersion';

export const useSentry = () => {
  const { config } = useConfig();
  const { version } = useVersion();

  useEffect(() => {
    if (!config.dsn) {
      console.warn('Sentry DSN is not configured. Skipping initialization.');
      return;
    }

    if (!config.sentryEnvironment) {
      console.warn(
        'Sentry environment is not configured. Skipping initialization.',
      );
      return;
    }

    const sentryConfig = {
      dsn: config.dsn,
      environment: config.sentryEnvironment,
      release: version,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: true,
          blockAllMedia: false,
        }),
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.feedbackIntegration({
          colorScheme: 'system',
          autoInject: false,
        }),
        // Dedupe integration prevents sending exact duplicate errors
        Sentry.dedupeIntegration(),
      ],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.25,
      // Max breadcrumbs to reduce payload size
      maxBreadcrumbs: 50,

      beforeSendTransaction(transaction: any): any {
        // Add screen and viewport data to contexts
        transaction.contexts = transaction.contexts || {};
        transaction.contexts.device = {
          ...transaction.contexts.device,
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
          viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        };

        // Comprehensive tags for debugging and analysis
        transaction.tags = {
          ...transaction.tags,
          // Screen & Viewport
          'screen.resolution': `${window.screen.width}x${window.screen.height}`,
          'viewport.size': `${window.innerWidth}x${window.innerHeight}`,
          'screen.orientation': window.screen.orientation?.type || 'unknown',
          'device.pixel_ratio': window.devicePixelRatio,

          // Device Type Detection
          'device.type':
            window.innerWidth < 768
              ? 'mobile'
              : window.innerWidth < 1024
                ? 'tablet'
                : 'desktop',
          'device.is_mobile': window.innerWidth < 768,

          // Browser & Performance
          'browser.online': navigator.onLine,
          'browser.language': navigator.language,
          'connection.type':
            (navigator as any).connection?.effectiveType || 'unknown',
          'connection.downlink':
            (navigator as any).connection?.downlink || 'unknown',

          // Memory (if available)
          'memory.limit': (performance as any).memory?.jsHeapSizeLimit
            ? Math.round(
                (performance as any).memory.jsHeapSizeLimit / 1048576,
              ) + 'MB'
            : 'unknown',
          'memory.used': (performance as any).memory?.usedJSHeapSize
            ? Math.round((performance as any).memory.usedJSHeapSize / 1048576) +
              'MB'
            : 'unknown',
        };

        return transaction;
      },
      beforeSendSpan(span: any): any {
        // Add key tags to all spans for filtering
        span.tags = {
          ...span.tags,
          'screen.resolution': `${window.screen.width}x${window.screen.height}`,
          'viewport.size': `${window.innerWidth}x${window.innerHeight}`,
          'device.type':
            window.innerWidth < 768
              ? 'mobile'
              : window.innerWidth < 1024
                ? 'tablet'
                : 'desktop',
          'browser.online': navigator.onLine,
          'connection.type':
            (navigator as any).connection?.effectiveType || 'unknown',
        };

        return span;
      },
    };

    Sentry.init(sentryConfig);
  }, [config, version]);
};

export const useSentryStatus = () => {
  const [isInitialized, setIsInitialized] = useState(() => {
    try {
      const client = Sentry.getClient();
      return !!client && !!client.getOptions();
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (isInitialized) return;

    const checkInterval = setInterval(() => {
      try {
        const client = Sentry.getClient();
        if (client && client.getOptions()) {
          setIsInitialized(true);
          clearInterval(checkInterval);
        }
      } catch {
        // Not ready yet
      }
    }, 100);

    return () => clearInterval(checkInterval);
  }, [isInitialized]);

  return isInitialized;
};
